import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  LayoutDashboard, Building2, Users, CalendarDays, BarChart3, Settings,
  UserPlus, DoorOpen, Clock, CreditCard, Mail, Globe, Monitor, FileText,
  Shield, MapPin, BookOpen, ArrowRight, CheckCircle2, AlertTriangle, Search,
  Megaphone, UserCheck
} from "lucide-react";

const SITE_URL = process.env.REACT_APP_BACKEND_URL;

function Section({ icon: Icon, title, children, id }) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-rig-orange/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-rig-orange" />
        </div>
        <h2 className="text-xl font-heading font-bold text-rig-charcoal">{title}</h2>
      </div>
      <div className="text-sm text-rig-charcoal/80 leading-relaxed space-y-3 pl-12">
        {children}
      </div>
    </section>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex gap-3 items-start">
      <span className="flex-shrink-0 h-6 w-6 rounded-full bg-rig-charcoal text-white text-xs font-bold flex items-center justify-center mt-0.5">{n}</span>
      <span>{children}</span>
    </div>
  );
}

const TOC = [
  { id: "overview", label: "Platform Overview", icon: BookOpen },
  { id: "dashboard", label: "Admin Dashboard", icon: LayoutDashboard },
  { id: "spaces", label: "Managing Spaces", icon: Building2 },
  { id: "bookings", label: "Bookings & Payments", icon: CalendarDays },
  { id: "cancellations", label: "Cancellation Policy", icon: AlertTriangle },
  { id: "managers", label: "Managers & Plans", icon: Users },
  { id: "members", label: "Members", icon: UserPlus },
  { id: "guests", label: "Guest Registry", icon: UserCheck },
  { id: "broadcast", label: "Broadcast Messaging", icon: Megaphone },
  { id: "authentication", label: "Sign-In & Emails", icon: Mail },
  { id: "cms", label: "Site Management (CMS)", icon: Settings },
  { id: "seo", label: "SEO & Analytics", icon: Search },
  { id: "display", label: "Room Display Screens", icon: Monitor },
  { id: "reports", label: "Reports & Export", icon: BarChart3 },
  { id: "roles", label: "User Roles", icon: Shield },
];

export default function AdminGuide() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin Guide | The Rig";
    if (!authLoading && !isAdmin) navigate("/login");
  }, [isAdmin, authLoading, navigate]);

  if (authLoading) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="admin-guide">
      <title>Admin Guide | The Rig</title>

      <div className="mb-10">
        <Badge className="bg-rig-teal/10 text-rig-teal border-0 mb-3">Admin Documentation</Badge>
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-rig-charcoal mb-2">
          Platform Guide
        </h1>
        <p className="text-rig-charcoal/60 text-base max-w-2xl">
          Everything you need to manage The Rig's coworking space booking platform — from spaces and bookings to CMS and reports.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
        {/* Table of Contents - sticky sidebar */}
        <nav className="lg:w-56 flex-shrink-0">
          <Card className="p-4 lg:sticky lg:top-24">
            <p className="text-xs font-bold uppercase tracking-wider text-rig-charcoal/40 mb-3">Contents</p>
            <ul className="space-y-1">
              {TOC.map(t => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className="flex items-center gap-2 text-sm text-rig-charcoal/70 hover:text-rig-orange py-1 transition-colors"
                  >
                    <t.icon className="h-3.5 w-3.5" />
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        </nav>

        {/* Guide Content */}
        <div className="flex-1 space-y-10">
          <Section icon={BookOpen} title="Platform Overview" id="overview">
            <p>
              <strong>The Rig</strong> is a coworking space booking platform centrally located in <strong>Emmastad, Willemstad</strong>. It supports four user roles (Admin, Manager, Member, Guest), hourly/daily/monthly bookings, and an integrated CMS for managing public content.
            </p>
            <p>All prices are displayed in <Badge variant="outline" className="text-xs mx-1">XCG</Badge> currency.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
              {[
                { label: "Meeting Rooms", val: "4" },
                { label: "Capacity", val: "30+" },
                { label: "Access", val: "24/7" },
                { label: "Booking", val: "Instant" },
              ].map(s => (
                <div key={s.label} className="bg-rig-charcoal/5 rounded-lg p-3 text-center">
                  <div className="font-heading font-extrabold text-lg text-rig-charcoal">{s.val}</div>
                  <div className="text-xs text-rig-charcoal/50">{s.label}</div>
                </div>
              ))}
            </div>
          </Section>

          <Separator />

          <Section icon={LayoutDashboard} title="Admin Dashboard" id="dashboard">
            <p>
              Access: <strong>Admin dropdown menu → Dashboard</strong> or navigate to <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">/admin</code>
            </p>
            <p>The dashboard shows at-a-glance stats:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Total bookings, active bookings, pending payments</li>
              <li>Total members and total revenue</li>
              <li>Recent bookings table with ability to confirm payments and update statuses</li>
              <li>Cancellation requests requiring admin approval</li>
            </ul>
          </Section>

          <Separator />

          <Section icon={Building2} title="Managing Spaces" id="spaces">
            <p>
              Access: <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">/admin/spaces</code>
            </p>
            <p><strong>Creating a space:</strong></p>
            <Step n={1}>Click "Add Space" and fill in: name, type (meeting room or office), description, capacity, and photos (image URLs).</Step>
            <Step n={2}>Set pricing: hourly, daily, and monthly rates in XCG.</Step>
            <Step n={3}>Assign layouts (boardroom, classroom, theater, U-shape, banquet, open) with their specific capacities.</Step>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-amber-800 text-xs flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Office Pricing</p>
              <p className="text-amber-700 mt-1">Spaces of type "office" automatically show <em>"Contact for pricing"</em> on public pages instead of actual rates.</p>
            </div>

            <p className="mt-2"><strong>Rented Out / Tenancy:</strong></p>
            <p>For long-term office rentals, use the "Rent Out" dialog on a space to assign a tenant, set contract dates. The system sends automated email reminders to admins at 6, 3, and 1 month before contract expiry.</p>
          </Section>

          <Separator />

          <Section icon={CalendarDays} title="Bookings & Payments" id="bookings">
            <p><strong>How bookings work:</strong></p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Guests</strong> can book without an account — they provide name and email. Booking starts as <Badge variant="outline" className="text-xs">pending_payment</Badge>.</li>
              <li><strong>Members</strong> with an active plan get covered hours deducted automatically. Booking is <Badge className="bg-green-100 text-green-800 border-0 text-xs">confirmed</Badge> instantly.</li>
              <li><strong>Manager members</strong> use their company plan quota.</li>
              <li>Users can optionally add a <strong>Meeting Name</strong> (e.g. "Weekly Standup") which appears on the room display screen and in confirmation emails.</li>
              <li><strong>Book on behalf:</strong> Managers and members can book rooms for their teammates. The "Booking For" dropdown appears in the booking flow when the user belongs to a team. Both booker and the person booked for receive confirmation emails.</li>
            </ul>

            <p className="mt-2"><strong>Confirming payments:</strong></p>
            <Step n={1}>Go to the Admin Dashboard.</Step>
            <Step n={2}>Find bookings with "pending_payment" status.</Step>
            <Step n={3}>Click the payment icon to confirm (cash, bank transfer, etc.).</Step>
            <p className="mt-1">A confirmation email with an <strong>.ics calendar invite</strong> is sent automatically to the booker.</p>

            <p className="mt-2"><strong>Editing bookings:</strong></p>
            <p>Admins can edit any active booking by clicking the pencil icon on the Admin Dashboard. You can change the meeting name, date, time, and notes. An updated confirmation email is sent automatically.</p>

            <p className="mt-2"><strong>Calendar integration:</strong></p>
            <p>Confirmed bookings include buttons to add to Google Calendar, Outlook, or download an .ics file.</p>
          </Section>

          <Separator />

          <Section icon={AlertTriangle} title="Cancellation Policy" id="cancellations">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="font-semibold text-red-800 text-sm">12-Hour Rule</p>
              <ul className="list-disc ml-4 space-y-1 text-red-700 mt-1">
                <li>Cancellations <strong>more than 12 hours</strong> before start time → automatically cancelled.</li>
                <li>Cancellations <strong>less than 12 hours</strong> before start time for <strong>paid bookings</strong> → requires admin approval.</li>
                <li>Unpaid bookings can always be cancelled immediately.</li>
              </ul>
            </div>
            <p className="mt-2">
              Pending cancellation requests appear on the Admin Dashboard with <strong>Approve</strong> / <strong>Deny</strong> buttons.
            </p>
          </Section>

          <Separator />

          <Section icon={Users} title="Managers & Plans" id="managers">
            <p>Access: <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">/admin/managers</code></p>
            <p><strong>Creating a manager:</strong></p>
            <Step n={1}>Click "Add Manager" — provide email, company name, and member quota.</Step>
            <Step n={2}>The system creates the account and assigns the "manager" role.</Step>
            <Step n={3}>Assign a membership plan to the manager for their team.</Step>

            <p className="mt-2"><strong>Plans:</strong></p>
            <p>Plans define what members can do. Each plan has:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Included hours</strong> — monthly hours covered by the plan</li>
              <li><strong>Room access</strong> — which rooms members can book</li>
              <li><strong>Price visibility</strong> — whether members see pricing</li>
              <li><strong>Price per hour</strong> in XCG</li>
            </ul>
            <p className="mt-1">Managers can add members (up to their quota), and those members inherit the plan's benefits.</p>
          </Section>

          <Separator />

          <Section icon={UserPlus} title="Members" id="members">
            <p>Access: <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">/admin/members</code></p>
            <ul className="list-disc ml-4 space-y-1">
              <li>View all member profiles with their plan, used hours, and contact info.</li>
              <li>Admins can create member profiles by linking a registered user.</li>
              <li>Deleting a member reverts their role to "guest."</li>
            </ul>
          </Section>

          <Separator />

          <Section icon={UserCheck} title="Guest Registry" id="guests">
            <p>Access: <strong>Managers tab</strong> → <strong>Guests</strong> tab</p>
            <p>The Rig tracks all visitors — both walk-in and pre-registered guests. Guests do not need to be tied to a room booking; they can be general visitors attached to a person.</p>

            <p className="mt-2"><strong>Pre-registering a guest (from anywhere):</strong></p>
            <p>Members and managers can pre-register guests directly from their dashboard via the <strong>"Register Guest"</strong> button — no booking required. Simply enter the guest's name, email, and purpose. If an email is provided, the guest receives a <strong>QR code</strong> they can scan to check in instantly.</p>

            <p className="mt-2"><strong>Pre-registering during a booking:</strong></p>
            <p>In step 3 of the booking flow, add guests under <strong>"Pre-register Guests."</strong> They receive an email invite with meeting details and a QR check-in code.</p>

            <p className="mt-2"><strong>Walk-in guests:</strong></p>
            <p>Visitors can self-register at the lobby kiosk (<strong>/kiosk</strong>). The kiosk has three modes:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Scan QR</strong> — Pre-registered guests point their email QR code at the iPad camera for instant check-in</li>
              <li><strong>Enter Code</strong> — Paste their check-in code from the email</li>
              <li><strong>New Guest</strong> — Walk-ins register with name, email, and who they're visiting</li>
            </ul>
            <p className="mt-1 text-xs text-muted-foreground">If a walk-in enters the same email as a pre-registered guest, the system automatically matches them — no duplicate.</p>

            <p className="mt-2"><strong>Saved/Frequent Guests:</strong></p>
            <p>Guests are automatically saved to the booker's contact book. Next time, use the <strong>"Quick add from frequent guests"</strong> search in the booking flow — one click, no retyping.</p>

            <p className="mt-2"><strong>Managing guests (admin):</strong></p>
            <ul className="list-disc ml-4 space-y-1">
              <li>The <strong>Guests tab</strong> shows all visitors with name, email, host, status, and check-in time.</li>
              <li>Use <strong>"Check Out"</strong> when a guest leaves.</li>
              <li>Statuses: <Badge className="bg-emerald-100 text-emerald-700 text-xs">checked in</Badge> <Badge className="bg-blue-100 text-blue-700 text-xs">expected</Badge> <Badge className="bg-gray-100 text-gray-700 text-xs">checked out</Badge></li>
            </ul>
          </Section>

          <Separator />

          <Section icon={Megaphone} title="Broadcast Messaging" id="broadcast">
            <p>Access: <strong>Managers tab</strong> → <strong>Broadcast</strong> tab</p>
            <p>Send messages to all managers, all members, or both. Messages are delivered <strong>in-app</strong> (appears in their dashboard inbox) and via <strong>branded email</strong>.</p>

            <p className="mt-2"><strong>Sending a broadcast:</strong></p>
            <Step n={1}>Go to the <strong>Broadcast</strong> tab.</Step>
            <Step n={2}>Select recipients: all managers & members, managers only, or members only.</Step>
            <Step n={3}>Write your subject and message content.</Step>
            <Step n={4}>Click <strong>"Send Broadcast"</strong>. Recipients see the message immediately in their dashboard and receive an email.</Step>

            <p className="mt-2"><strong>Tracking:</strong></p>
            <p>Sent broadcasts show recipient count and how many have read the message.</p>
          </Section>

          <Separator />

          <Section icon={Mail} title="Sign-In & User Emails" id="authentication">
            <p><strong>Passwordless sign-in (OTP):</strong></p>
            <p>All users can sign in using a <strong>one-time passcode</strong> instead of a password. On the login page, they click <strong>"Sign in with passcode"</strong>, enter their email, and receive a 6-digit code that expires in 5 minutes.</p>

            <p className="mt-2"><strong>Automated emails on account creation:</strong></p>
            <p>When you create a new <strong>Manager</strong> or when a Manager adds a <strong>Member</strong>, two emails are automatically sent via Resend:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Welcome email</strong> — introduces the user to The Rig and explains their role's capabilities</li>
              <li><strong>Credentials email</strong> — sent separately with their login email and password, plus a note recommending they change it or use the passcode option</li>
            </ul>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-green-800 text-xs flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> No Action Needed</p>
              <p className="text-green-700 mt-1">Emails are sent automatically — you don't need to manually share credentials with new users.</p>
            </div>
          </Section>

          <Separator />

          <Section icon={Settings} title="Site Management (CMS)" id="cms">
            <p>Access: <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">/admin/cms</code></p>
            <p>The CMS has <strong>6 tabs</strong>:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
              {[
                { tab: "Homepage", desc: "Hero section text, images, CTA buttons, and feature cards" },
                { tab: "About Page", desc: "Hero image, mission, story, team section, and stats" },
                { tab: "Contact Page", desc: "Email, phone, hours, address, and Google Maps embed" },
                { tab: "Tenants", desc: "Logo wall — add tenant logos shown on the homepage" },
                { tab: "SEO", desc: "Meta titles, descriptions, OG tags, and FAQ schema" },
                { tab: "GEO", desc: "Local business schema for Google Maps/Local Pack" },
              ].map(c => (
                <div key={c.tab} className="bg-rig-charcoal/5 rounded-lg p-3">
                  <p className="font-semibold text-sm text-rig-charcoal">{c.tab}</p>
                  <p className="text-xs text-rig-charcoal/60 mt-0.5">{c.desc}</p>
                </div>
              ))}
            </div>
          </Section>

          <Separator />

          <Section icon={Search} title="SEO & Analytics" id="seo">
            <p><strong>Per-page SEO</strong> — each public page has unique title, meta description, and Open Graph tags for social sharing:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Homepage</strong> — driven by CMS SEO tab (admin editable)</li>
              <li><strong>Spaces, Space Detail, Booking</strong> — dynamic tags from space data</li>
              <li><strong>About, Contact, Login</strong> — static, contextual tags</li>
            </ul>
            <p className="mt-2"><strong>Google Analytics</strong> — GA4 is integrated and tracks page views on every route change. View analytics at <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-rig-orange underline">analytics.google.com</a>.</p>
            <p><strong>Sitemap</strong> — auto-generated at <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">/api/sitemap.xml</code> for search engine indexing.</p>
          </Section>

          <Separator />

          <Section icon={Monitor} title="Room Display Screens" id="display">
            <p>Each meeting room has an e-ink-friendly display screen showing real-time booking status.</p>
            <p><strong>How to set up a display:</strong></p>
            <Step n={1}>A display device is registered per room (auto-created for all meeting rooms).</Step>
            <Step n={2}>Access the display at: <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">{SITE_URL}/display/[space_id]</code></Step>
            <Step n={3}>The screen shows current status (Available / In Use / Upcoming), a QR code for instant booking, and today's schedule.</Step>
            <p className="mt-1">Displays are designed for e-ink screens — high contrast, no animations, auto-refresh.</p>
          </Section>

          <Separator />

          <Section icon={BarChart3} title="Reports & Export" id="reports">
            <p>Access: <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">/admin/reports</code></p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Billable Report</strong> — all billable hours by user and space</li>
              <li><strong>Revenue Report</strong> — income by space, period, and payment method</li>
              <li><strong>Utilization Report</strong> — room usage rates and peak hours</li>
            </ul>
            <p className="mt-1">Reports can be exported as <strong>CSV</strong> or <strong>Excel</strong> files using the export button.</p>
          </Section>

          <Separator />

          <Section icon={Shield} title="User Roles" id="roles">
            <div className="space-y-3 mt-1">
              {[
                { role: "Admin", color: "bg-red-100 text-red-800", desc: "Full platform access. Manage spaces, bookings, users, CMS, reports. Hardcoded email whitelist." },
                { role: "Manager", color: "bg-blue-100 text-blue-800", desc: "Manages a team of members under a plan. Can add members (up to quota), view team bookings, message admins." },
                { role: "Member", color: "bg-green-100 text-green-800", desc: "Books spaces using plan-covered hours. Views own bookings and profile." },
                { role: "Guest", color: "bg-gray-100 text-gray-800", desc: "Books without an account (name + email). Pays per booking. No dashboard." },
              ].map(r => (
                <div key={r.role} className="flex gap-3 items-start">
                  <Badge className={`${r.color} border-0 text-xs min-w-[70px] justify-center`}>{r.role}</Badge>
                  <span className="text-sm">{r.desc}</span>
                </div>
              ))}
            </div>
          </Section>

          <Separator />

          <div className="text-center py-8 text-rig-charcoal/40 text-sm">
            <p>Need help? Contact <a href="mailto:info@ic-foundation.org" className="text-rig-orange underline">info@ic-foundation.org</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
